import { api, ApiSchema } from "@/modules/api";

// Status enums
export type AudioRecordingUploadStatus = ApiSchema["AudioRecordingUploadStatus"];
export type AudioRecordingStatus = ApiSchema["AudioRecordingStatus"];
export type AudioRecordingProcessingStatus = ApiSchema["AudioRecordingProcessingStatus"];

export type AudioRecordingInternalResponseSchema =
  ApiSchema["AudioRecordingInternalResponseSchema"];
export type CreateAudioRecordingInternalRequestSchema =
  ApiSchema["CreateAudioRecordingInternalRequestSchema"];
export type CreateAudioRecordingInternalResponseSchema =
  ApiSchema["CreateAudioRecordingInternalResponseSchema"];
export type RegisterAudioRecordingUploadRequestSchema =
  ApiSchema["RegisterAudioRecordingUploadRequestSchema"];
export type RegisterAudioRecordingUploadResponseSchema =
  ApiSchema["RegisterAudioRecordingUploadResponseSchema"];
export type FinalizeAudioRecordingUploadRequestSchema =
  ApiSchema["FinalizeAudioRecordingUploadRequestSchema"];
export type GetAudioRecordingDownloadUrlResponseSchema =
  ApiSchema["GetAudioRecordingDownloadUrlResponseSchema"];
export type SearchAudioRecordingsInternalRequestSchema =
  ApiSchema["SearchAudioRecordingsInternalRequestSchema"];
export type SearchAudioRecordingsInternalResponseSchema =
  ApiSchema["SearchAudioRecordingsInternalResponseSchema"];
export type AudioRecordingSummaryInternalResponseSchema =
  ApiSchema["AudioRecordingSummaryInternalResponseSchema"];
export type AudioRecordingEventInternalResponseSchema =
  ApiSchema["AudioRecordingEventInternalResponseSchema"];
export type GetAudioRecordingEventsInternalResponseSchema =
  ApiSchema["GetAudioRecordingEventsInternalResponseSchema"];
// Quality metrics types - defined locally since the API schema may not be regenerated yet
export type AudioRecordingState = "success" | "failed" | "pending" | "stale" | "no_content";
export type AudioRecordingClassification = "warning" | "failure";
export type AudioRecordingQualityPlatformKind = "ALL" | "web" | "macos" | "windows" | "ios";

export interface AudioRecordingEventSummary {
  id: string;
  kind: string;
  occurred_at: string;
  properties_json: Record<string, unknown>;
}

export interface AudioRecordingMetrics {
  state: AudioRecordingState;
  classification: AudioRecordingClassification | null;
  platform_kind: string | null;
  audio_duration_seconds: number | null;
  upload_duration: number | null;
  transform_duration: number | null;
  finalization_duration: number | null;
  warning_reasons: string[];
  failure_reasons: string[];
}

export interface AudioRecordingQualityData {
  events: AudioRecordingEventSummary[];
  metrics: AudioRecordingMetrics;
}

export interface AudioRecordingQualityMetricsInternalResponseSchema {
  window_start: string;
  window_end: string;
  recordings: Record<string, AudioRecordingQualityData>;
}
export type RegisterAudioRecordingUploadInternalRequestSchema =
  ApiSchema["RegisterAudioRecordingUploadInternalRequestSchema"];
export type RegisterAudioRecordingUploadInternalResponseSchema =
  ApiSchema["RegisterAudioRecordingUploadInternalResponseSchema"];
export type FinalizeAudioRecordingUploadInternalRequestSchema =
  ApiSchema["FinalizeAudioRecordingUploadInternalRequestSchema"];
export type FinalizeAudioRecordingUploadInternalResponseSchema =
  ApiSchema["FinalizeAudioRecordingUploadInternalResponseSchema"];
export type RecallDataInternalResponseSchema = ApiSchema["RecallDataInternalResponseSchema"];

export const createAudioRecordingInternal = async (
  payload: CreateAudioRecordingInternalRequestSchema
): Promise<AudioRecordingInternalResponseSchema | null> => {
  const response = await api.post("/internal/v1/audio-recordings/create", {
    body: payload,
  });

  return response.data?.audio_recording ?? null;
};

export const fetchAudioRecordingInternal = async (
  audioRecordingId: string
): Promise<AudioRecordingInternalResponseSchema | null> => {
  const response = await api.get("/internal/v1/audio-recordings/{audio_recording_id}", {
    params: {
      path: {
        audio_recording_id: audioRecordingId,
      },
    },
  });

  return response.data ?? null;
};

export const registerAudioRecordingUpload = async (
  audioRecordingId: string,
  payload: RegisterAudioRecordingUploadRequestSchema
): Promise<RegisterAudioRecordingUploadResponseSchema | null> => {
  const response = await api.post("/v2/audio-recordings/{audio_recording_id}/register-upload", {
    params: {
      path: {
        audio_recording_id: audioRecordingId,
      },
    },
    body: payload,
  });

  return response.data ?? null;
};

export const finalizeAudioRecordingUpload = async (
  audioRecordingId: string,
  payload: FinalizeAudioRecordingUploadRequestSchema
) => {
  return api.post("/v2/audio-recordings/{audio_recording_id}/finalize-upload", {
    params: {
      path: {
        audio_recording_id: audioRecordingId,
      },
    },
    body: payload,
  });
};

export const getAudioRecordingDownloadUrl = async (
  audioRecordingId: string
): Promise<GetAudioRecordingDownloadUrlResponseSchema | null> => {
  const response = await api.get("/v2/audio-recordings/{audio_recording_id}/download-url", {
    params: {
      path: {
        audio_recording_id: audioRecordingId,
      },
    },
  });

  return response.data ?? null;
};

export const searchAudioRecordingsInternal = async (
  params: SearchAudioRecordingsInternalRequestSchema
): Promise<SearchAudioRecordingsInternalResponseSchema | null> => {
  const response = await api.post("/internal/v1/audio-recordings/search", {
    body: params,
  });

  return response.data ?? null;
};

export const fetchAudioRecordingEventsInternal = async (
  audioRecordingId: string
): Promise<GetAudioRecordingEventsInternalResponseSchema | null> => {
  const response = await api.get("/internal/v1/audio-recordings/{audio_recording_id}/events", {
    params: {
      path: {
        audio_recording_id: audioRecordingId,
      },
    },
  });

  return response.data ?? null;
};

export const fetchAudioRecordingQualityMetricsInternal =
  async (): Promise<AudioRecordingQualityMetricsInternalResponseSchema | null> => {
    const response = await api.post("/internal/v1/audio-recordings/quality-metrics", {});

    // Cast through unknown since the OpenAPI types may not be regenerated yet
    return response.data as unknown as AudioRecordingQualityMetricsInternalResponseSchema | null;
  };

export const registerAudioRecordingUploadInternal = async (
  audioRecordingId: string,
  payload: RegisterAudioRecordingUploadInternalRequestSchema
): Promise<RegisterAudioRecordingUploadInternalResponseSchema | null> => {
  const response = await api.post(
    "/internal/v1/audio-recordings/{audio_recording_id}/register-upload",
    {
      params: {
        path: {
          audio_recording_id: audioRecordingId,
        },
      },
      body: payload,
    }
  );

  return response.data ?? null;
};

export const finalizeAudioRecordingUploadInternal = async (
  audioRecordingId: string,
  payload: FinalizeAudioRecordingUploadInternalRequestSchema
): Promise<FinalizeAudioRecordingUploadInternalResponseSchema | null> => {
  const response = await api.post(
    "/internal/v1/audio-recordings/{audio_recording_id}/finalize-upload",
    {
      params: {
        path: {
          audio_recording_id: audioRecordingId,
        },
      },
      body: payload,
    }
  );

  return response.data ?? null;
};

export const fetchAudioRecordingRecallData = async (
  audioRecordingId: string
): Promise<RecallDataInternalResponseSchema | null> => {
  const response = await api.get("/internal/v1/audio-recordings/{audio_recording_id}/recall-data", {
    params: {
      path: {
        audio_recording_id: audioRecordingId,
      },
    },
  });

  return response.data ?? null;
};
